import { useState, useRef } from 'react';
import { Music, Upload, CheckCircle, ChevronDown } from 'lucide-react';
import { useThemeClasses } from '../hooks/useTheme';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const GENRES = [
  'Hip-Hop', 'R&B', 'Afrobeats', 'Soul', 'Gospel',
  'Jazz', 'Neo-Soul', 'Trap', 'Drill', 'Lo-Fi', 'Other',
];

const PLATFORMS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'twitter', label: 'Twitter / X' },
  { value: 'tiktok', label: 'TikTok' },
];

export default function FoundingArtistPage() {
  const { isDark, textClass, textMutedClass, borderClass } = useThemeClasses();

  const [form, setForm] = useState({
    stage_name: '',
    email: '',
    track_title: '',
    bio: '',
    social_handle: '',
    social_platform: 'instagram',
    agreed_to_terms: false,
  });
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [trackFile, setTrackFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const trackInputRef = useRef(null);
  const coverInputRef = useRef(null);

  const inputClass = cn(
    "w-full px-4 py-3 text-sm border rounded-none outline-none transition-colors",
    isDark
      ? 'bg-zinc-900 border-white/10 text-white placeholder:text-white/20 focus:border-amber-500'
      : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-amber-500'
  );

  const labelClass = cn("block text-[11px] font-display tracking-widest uppercase mb-1.5", textMutedClass);

  const toggleGenre = (genre) => {
    setSelectedGenres(prev =>
      prev.includes(genre) ? prev.filter(g => g !== genre) : [...prev, genre]
    );
  };

  const handleTrackSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error('Track must be under 20 MB');
      return;
    }
    setTrackFile(file);
  };

  const handleCoverSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Cover art must be under 15 MB');
      return;
    }
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.stage_name.trim()) return toast.error('Artist name is required');
    if (!form.email.trim()) return toast.error('Email is required');
    if (!form.track_title.trim()) return toast.error('Track title is required');
    if (selectedGenres.length === 0) return toast.error('Select at least one genre');
    if (!trackFile) return toast.error('Upload your track file');
    if (!form.social_handle.trim()) return toast.error('Social handle is required');
    if (!form.agreed_to_terms) return toast.error('You must agree to the terms');

    setSubmitting(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === 'agreed_to_terms') data.append(k, String(v));
        else data.append(k, v);
      });
      data.append('genre_tags', selectedGenres.join(','));
      data.append('track_file', trackFile);
      if (coverFile) data.append('cover_art', coverFile);

      await axios.post(`${API}/api/music/submit`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center py-16">
          <div className="w-20 h-20 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-amber-500" />
          </div>
          <h1 className={cn("text-2xl font-display tracking-wider mb-4", textClass)}>
            You're in. 📀
          </h1>
          <p className={cn("text-sm leading-relaxed", textMutedClass)}>
            Welcome to the BLVX Founding Artist Program. We'll review your submission
            and be in touch within <span className="text-amber-500 font-medium">5 business days</span>.
          </p>
          <p className={cn("text-xs mt-4", textMutedClass)}>
            Check your email for a confirmation.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-4 py-8 pb-24">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <Music className="h-4 w-4 text-amber-500" />
          </div>
          <span className={cn("text-[11px] font-display tracking-widest uppercase", textMutedClass)}>
            Founding Artist Program
          </span>
        </div>
        <h1 className={cn("text-2xl font-display tracking-wide mb-2", textClass)}>
          Put your music on the block.
        </h1>
        <p className={cn("text-sm leading-relaxed", textMutedClass)}>
          BLVX is building a music library where indie artists from the culture can
          reach their people. Phase 1 is free. Your music, your ownership —
          we just help distribute it inside the platform.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">

        {/* Artist name */}
        <div>
          <label className={labelClass}>Artist Name (Stage Name)</label>
          <input
            className={inputClass}
            placeholder="How your fans know you"
            value={form.stage_name}
            onChange={e => setForm(f => ({ ...f, stage_name: e.target.value }))}
          />
        </div>

        {/* Email */}
        <div>
          <label className={labelClass}>Contact Email</label>
          <input
            type="email"
            className={inputClass}
            placeholder="you@example.com"
            value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
          />
        </div>

        {/* Track title */}
        <div>
          <label className={labelClass}>Track Title</label>
          <input
            className={inputClass}
            placeholder="What's the track called?"
            value={form.track_title}
            onChange={e => setForm(f => ({ ...f, track_title: e.target.value }))}
          />
        </div>

        {/* Genre tags */}
        <div>
          <label className={labelClass}>Genre Tags — pick all that apply</label>
          <div className="flex flex-wrap gap-2">
            {GENRES.map(g => (
              <button
                key={g}
                type="button"
                onClick={() => toggleGenre(g)}
                className={cn(
                  "px-3 py-1.5 text-xs font-display tracking-wider uppercase border transition-colors",
                  selectedGenres.includes(g)
                    ? 'bg-amber-500 border-amber-500 text-black'
                    : isDark
                      ? 'border-white/10 text-white/40 hover:text-white'
                      : 'border-gray-200 text-gray-400 hover:text-gray-900'
                )}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        {/* Track file upload */}
        <div>
          <label className={labelClass}>Track File (MP3 or WAV, max 20 MB)</label>
          <input ref={trackInputRef} type="file" accept="audio/mp3,audio/mpeg,audio/wav,.mp3,.wav" onChange={handleTrackSelect} className="hidden" />
          <button
            type="button"
            onClick={() => trackInputRef.current?.click()}
            className={cn(
              "w-full flex items-center gap-3 px-4 py-3 border text-sm transition-colors",
              trackFile
                ? 'border-amber-500 text-amber-500'
                : isDark
                  ? 'border-white/10 text-white/40 hover:text-white hover:border-white/20'
                  : 'border-gray-200 text-gray-400 hover:text-gray-900'
            )}
          >
            <Upload className="h-4 w-4 shrink-0" />
            <span className="truncate">
              {trackFile ? trackFile.name : 'Upload your track'}
            </span>
          </button>
        </div>

        {/* Cover art */}
        <div>
          <label className={labelClass}>Cover Art (JPG/PNG, min 1000×1000) — optional</label>
          <div className="flex items-start gap-3">
            <input ref={coverInputRef} type="file" accept="image/jpeg,image/png" onChange={handleCoverSelect} className="hidden" />
            <button
              type="button"
              onClick={() => coverInputRef.current?.click()}
              className={cn(
                "flex-1 flex items-center gap-3 px-4 py-3 border text-sm transition-colors",
                coverFile
                  ? 'border-amber-500 text-amber-500'
                  : isDark
                    ? 'border-white/10 text-white/40 hover:text-white'
                    : 'border-gray-200 text-gray-400 hover:text-gray-900'
              )}
            >
              <Upload className="h-4 w-4 shrink-0" />
              <span className="truncate">
                {coverFile ? coverFile.name : 'Upload cover art'}
              </span>
            </button>
            {coverPreview && (
              <img src={coverPreview} alt="Cover preview" className="w-16 h-16 object-cover border border-white/10" />
            )}
          </div>
        </div>

        {/* Bio */}
        <div>
          <label className={labelClass}>Artist Bio (max 280 characters)</label>
          <textarea
            className={cn(inputClass, "resize-none")}
            rows={3}
            placeholder="Tell the culture who you are..."
            maxLength={280}
            value={form.bio}
            onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
          />
          <p className={cn("text-[10px] text-right mt-1", textMutedClass)}>
            {form.bio.length}/280
          </p>
        </div>

        {/* Social */}
        <div>
          <label className={labelClass}>Social Handle</label>
          <div className="flex gap-2">
            <div className="relative">
              <select
                className={cn(
                  "h-full px-3 py-3 text-xs border appearance-none pr-7",
                  isDark ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900'
                )}
                value={form.social_platform}
                onChange={e => setForm(f => ({ ...f, social_platform: e.target.value }))}
              >
                {PLATFORMS.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              <ChevronDown className={cn("absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 pointer-events-none", textMutedClass)} />
            </div>
            <input
              className={cn(inputClass, "flex-1")}
              placeholder="@yourhandle"
              value={form.social_handle}
              onChange={e => setForm(f => ({ ...f, social_handle: e.target.value }))}
            />
          </div>
        </div>

        {/* Terms agreement */}
        <div className={cn("p-4 border rounded-none", isDark ? 'border-white/10 bg-white/2' : 'border-gray-200 bg-gray-50')}>
          <label className="flex items-start gap-3 cursor-pointer">
            <div
              className={cn(
                "mt-0.5 w-5 h-5 shrink-0 flex items-center justify-center border transition-colors",
                form.agreed_to_terms
                  ? 'bg-amber-500 border-amber-500'
                  : isDark ? 'border-white/20 bg-transparent' : 'border-gray-300 bg-white'
              )}
              onClick={() => setForm(f => ({ ...f, agreed_to_terms: !f.agreed_to_terms }))}
            >
              {form.agreed_to_terms && (
                <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <p className={cn("text-xs leading-relaxed", textMutedClass)}>
              I own or control all rights to this track and grant BLVX a non-exclusive license to use it within
              the platform for user-generated content. I retain full ownership of my music.
            </p>
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting}
          className={cn(
            "w-full py-4 text-sm font-display tracking-widest uppercase transition-opacity",
            "bg-amber-500 text-black hover:bg-amber-400",
            submitting && 'opacity-60 cursor-not-allowed'
          )}
        >
          {submitting ? 'Uploading your track...' : 'Join the Founding Artist Program'}
        </button>

        <p className={cn("text-xs text-center", textMutedClass)}>
          Phase 1 is free. No revenue share yet — that's coming in Phase 2.
          You'll be the first to know.
        </p>
      </form>
    </div>
  );
}
